/**
 * TopazPadCapture (CG-F, PROD-1064) — the on-pad signing surface for the
 * document-centric consent flow. Mounts a canvas, arms a
 * {@link TopazSignatureProvider} (SigWeb bridge) so the signer's ink from the
 * physical Topaz pad renders live, and on "Use signature" normalises the
 * capture to a PNG data-URL — the SAME contract the DS `SignaturePad` emits, so
 * the parent modal treats canvas and pad identically.
 *
 * Only rendered when a Topaz pad is actually available (the popup probes
 * SigWeb first), so this never appears on a machine without the hardware.
 *
 * @onDevice — the live-ink + capture path runs against Topaz SigWeb hardware
 * that lives on another machine; validated on-device (see SigWebApi notes).
 */
import { useEffect, useRef, useState } from 'react';
import { TopazSignatureProvider } from '@/consent/signature';

/** Vendored Topaz `SigWebTablet.js` (drop the Topaz-provided file at this path
 *  under `public/`). Absent → the provider reports unavailable and this
 *  surface is never shown. */
export const SIGWEB_SCRIPT_URL = '/vendor/SigWebTablet.js';

/** Blob → `data:image/png;base64,…` (the value shape the DS SignaturePad + the
 *  sign body use). */
function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error('Could not read signature.'));
    reader.readAsDataURL(blob);
  });
}

export interface TopazPadCaptureProps {
  dir?: 'ltr' | 'rtl';
  /** Fires with the captured PNG data-URL (or null on clear). */
  onCapture: (png: string | null) => void;
}

export function TopazPadCapture({ dir = 'ltr', onCapture }: TopazPadCaptureProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const providerRef = useRef<TopazSignatureProvider | null>(null);
  const [empty, setEmpty] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const provider = new TopazSignatureProvider(canvas, { scriptUrl: SIGWEB_SCRIPT_URL });
    providerRef.current = provider;
    let disposed = false;

    provider.start().catch(() => {
      if (!disposed) setError('Could not start the signature pad. Check it is connected.');
    });
    // SigWeb has no "stroke" event, so poll the point count to enable capture.
    const poll = window.setInterval(() => {
      if (!disposed) setEmpty(provider.isEmpty());
    }, 300);

    return () => {
      disposed = true;
      window.clearInterval(poll);
      provider.dispose();
    };
  }, []);

  const handleClear = () => {
    providerRef.current?.clear();
    setEmpty(true);
    setError(null);
    onCapture(null);
  };

  const handleUse = async () => {
    const provider = providerRef.current;
    if (!provider) return;
    setBusy(true);
    setError(null);
    try {
      const result = await provider.getResult();
      // PNG only. The pad's forensic `.SIG` biometric (`result.biometric`) is
      // intentionally NOT read or persisted in this pass — it mirrors the canvas
      // capability shipping `biometricStrokes: null` (DS stroke pass-through is
      // scrips-signal-ds#89). So no biometric PHI leaves the client here; when
      // #89 lands, thread `result.biometric` through onCapture + the sign body
      // together with the canvas strokes, under NABIDH-compliant storage.
      onCapture(await blobToDataUrl(result.png));
    } catch {
      setError('No signature captured yet — sign on the pad, then try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div dir={dir} className="flex flex-col gap-3">
      <div className="rounded-md border border-border-default bg-surface-card overflow-hidden">
        <canvas ref={canvasRef} width={440} height={180} className="block w-full" />
      </div>
      <p className="text-xs text-text-muted">
        Sign on the connected Topaz pad — your signature appears above.
      </p>
      {error ? <p className="text-xs text-status-error">{error}</p> : null}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={handleClear}
          className="h-10 px-4 rounded-md border border-border-strong text-sm font-semibold text-text-muted hover:bg-grey-100 hover:text-text-primary"
        >
          Clear
        </button>
        <button
          type="button"
          disabled={empty || busy}
          onClick={handleUse}
          className="h-10 px-4 rounded-md bg-brand-primary text-white text-sm font-semibold disabled:opacity-50"
        >
          {busy ? 'Capturing…' : 'Use signature'}
        </button>
      </div>
    </div>
  );
}

TopazPadCapture.displayName = 'TopazPadCapture';
